import { useEffect, useState } from "react";
import { API_GET_PROJECTS_BY_EMPLOYEE_ID, GROWTH_QUESTIONS, USER_PROFILE } from "../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../utils/Storage.js";
import { ApiError, getApi } from "../../../utils/api.ts";
import { currentProjects, type ContributionQuestion, type CurrentProject, type EmployeeProject } from "./contribution-data.ts";

type ProfileResponse = { statusCode: number; data: { personalDetail: { id: number } } };
type ProjectsResponse = { statusCode: number; message?: string; data: { projects: EmployeeProject[] } };
type ContributionContext = { employeeId: number; questions: ContributionQuestion[]; projects: CurrentProject[] };

export function useContributionContext() {
  const [context, setContext] = useState<ContributionContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    setContext(null);

    async function load() {
      try {
        const token = getAuthToken("accessToken");
        if (!token) throw new Error("Your session has expired. Please sign in again.");
        // The current JWT contains an email subject. The server resolves the ID from it.
        const [profile, questions] = await Promise.all([
          getApi<ProfileResponse>(USER_PROFILE, token, "json", controller.signal),
          getApi<ContributionQuestion[]>(`${GROWTH_QUESTIONS}?growth_category=CONTRIBUTION`, token, "json", controller.signal),
        ]);
        const employeeId = profile.data?.data?.personalDetail?.id;
        if (!Number.isInteger(employeeId) || employeeId <= 0) throw new Error("Could not identify your employee profile. Please sign in again.");
        if (!Array.isArray(questions.data)) throw new Error("Could not load the contribution questions. Please try again.");
        let projects: EmployeeProject[] = [];
        try {
          const response = await getApi<ProjectsResponse>(`${API_GET_PROJECTS_BY_EMPLOYEE_ID}/${employeeId}`, token, "json", controller.signal);
          if (response.data.statusCode !== 200 || !Array.isArray(response.data.data?.projects)) {
            throw new Error("Could not load your current projects. Please try again.");
          }
          projects = response.data.data.projects;
        } catch (cause) {
          if (!(cause instanceof ApiError && cause.status === 404 && /no projects? found/i.test(cause.message))) throw cause;
        }
        if (!controller.signal.aborted) setContext({ employeeId, questions: questions.data, projects: currentProjects(projects, employeeId) });
      } catch (cause) {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Could not load your contribution form.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    void load();
    return () => controller.abort();
  }, [attempt]);

  return { context, loading, error, retry: () => setAttempt((value) => value + 1) };
}
